import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { publicApi } from "../lib/api";
import ListingCard from "../components/ListingCard";
import Breadcrumb from "../components/Breadcrumb";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

const Loading = () => (
  <div className="pt-40 min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#12964A]" /></div>
);

const ErrorState = ({ message }) => (
  <div className="pt-40 min-h-[60vh] px-6">
    <div className="max-w-2xl mx-auto text-center">
      <h1 className="font-display font-bold text-4xl text-[#1F2937]">{message}</h1>
      <p className="text-slate-500 mt-4">La categoria richiesta non è al momento disponibile.</p>
      <Link to="/attivita" className="inline-flex items-center gap-2 mt-8 text-[#12964A] hover:underline">
        <ArrowLeft className="w-4 h-4" /> Torna alle attività
      </Link>
    </div>
  </div>
);

const SubcategoryChip = ({ categorySlug, sub, count }) => (
  <Link
    to={`/categoria/${categorySlug}/${sub.slug}`}
    className="group flex items-center justify-between px-5 py-4 bg-white rounded-2xl border border-[#E5E7EB] hover:border-[#12964A] hover:bg-[#F0FAF3] transition-colors"
  >
    <div>
      <div className="font-display font-semibold text-[#1F2937] group-hover:text-[#12964A]">{sub.name}</div>
      <div className="text-xs text-slate-500 mt-0.5">{count} attività</div>
    </div>
    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#12964A] transition-transform group-hover:translate-x-0.5" />
  </Link>
);

const resolveErrorMessage = (err) => {
  if (err?.response?.status === 404) return "Categoria non trovata";
  return err?.response?.data?.detail || "Errore nel caricamento.";
};

const Category = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [subs, setSubs] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    Promise.all([
      publicApi.getCategory(slug),
      publicApi.listSubcategories(slug),
      publicApi.listBusinesses({ category: slug, limit: 200, order: "name" }),
    ])
      .then(([c, s, b]) => {
        if (cancelled) return;
        setCategory(c.data);
        setSubs((s.data || []).filter((x) => x.published !== false));
        setBusinesses(b.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("Category load failed", err);
        setError(resolveErrorMessage(err));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const countPerSub = React.useMemo(() => {
    const map = {};
    businesses.forEach((b) => {
      (b.business_subcategories || []).forEach((bs) => {
        const s = bs.subcategory;
        if (!s) return;
        map[s.slug] = (map[s.slug] || 0) + 1;
      });
    });
    return map;
  }, [businesses]);

  // Mostra solo sottocategorie con almeno 1 attività pubblicata.
  // Il DB non viene toccato: le sottocategorie vuote restano disponibili in admin
  // e ricompariranno automaticamente non appena riceveranno la prima published.
  const visibleSubs = React.useMemo(
    () => subs.filter((s) => (countPerSub[s.slug] || 0) > 0),
    [subs, countPerSub]
  );

  if (loading) return <Loading />;
  if (error || !category) return <ErrorState message={error || "Categoria non trovata"} />;

  return (
    <div className="pt-32">
      <section className="pb-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Attività", to: "/attivita" }, { label: category.name }]} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mt-6">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-4">Categoria</div>
              <h1 className="font-display font-bold text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#1F2937]">{category.name}</h1>
              {category.description && (
                <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl">{category.description}</p>
              )}
              <p className="mt-6 text-sm text-slate-500">
                <strong className="text-[#12964A]">{businesses.length}</strong> attività in <strong>{visibleSubs.length}</strong> sottocategorie
              </p>
            </div>
            {category.cover_image_url && (
              <div className="lg:col-span-5">
                <div className="rounded-[32px] overflow-hidden aspect-[4/3]">
                  <img src={category.cover_image_url} alt={category.name} loading="eager" fetchPriority="high" className="w-full h-full object-cover" />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {visibleSubs.length > 0 && (
        <section className="py-14 bg-[#F8F9FA]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-[#1F2937] mb-6">Sottocategorie</h2>
            <div data-testid="subcategories-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {visibleSubs.map((s) => (
                <SubcategoryChip key={s.id || s.slug} categorySlug={slug} sub={s} count={countPerSub[s.slug] || 0} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-[#1F2937] mb-6">Tutte le attività</h2>
          {businesses.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-display text-2xl text-[#1F2937]">Nessuna attività pubblicata al momento.</p>
              <p className="text-slate-500 mt-3">Torna presto: la directory viene aggiornata continuamente.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((b) => <ListingCard key={b.id || b.slug} listing={b} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Category;
